import { format, parseISO } from 'date-fns';
import { toZonedTime, fromZonedTime, formatInTimeZone } from 'date-fns-tz';

// CDT timezone constant
export const CDT_TIMEZONE = 'America/Chicago';

/**
 * Convert a date to CDT timezone
 * @param {Date|string} date - Date to convert
 * @returns {Date} Date in CDT timezone
 */
export const toCDT = (date) => {
  if (!date) return null;
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return toZonedTime(dateObj, CDT_TIMEZONE);
};

/**
 * Convert a date from CDT to UTC
 * @param {Date|string} date - Date in CDT
 * @returns {Date} Date in UTC
 */
export const fromCDT = (date) => {
  if (!date) return null;
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return fromZonedTime(dateObj, CDT_TIMEZONE);
};

/**
 * Format a date in CDT timezone
 * @param {Date|string} date - Date to format
 * @param {string} formatStr - Format string
 * @returns {string} Formatted date string
 */
export const formatInCDT = (date, formatStr = 'yyyy-MM-dd HH:mm:ss') => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return formatInTimeZone(dateObj, CDT_TIMEZONE, formatStr);
};

/**
 * Get current date in CDT
 * @returns {Date} Current date in CDT
 */
export const nowInCDT = () => {
  return toZonedTime(new Date(), CDT_TIMEZONE);
};

/**
 * Format date for display (MM/DD/YYYY)
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateForDisplay = (date) => {
  if (!date) return 'N/A';
  
  // If it's a date-only string (YYYY-MM-DD format from database), handle it directly
  // to avoid timezone conversion issues
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    // It's a DATEONLY field, just reformat without timezone conversion
    const [year, month, day] = date.split('-');
    return `${month}/${day}/${year}`;
  }
  
  return formatInCDT(date, 'MM/dd/yyyy');
};

/**
 * Format datetime for display (MM/DD/YYYY HH:MM)
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted datetime string
 */
export const formatDateTimeForDisplay = (date) => {
  if (!date) return 'N/A';
  return formatInCDT(date, 'MM/dd/yyyy HH:mm');
};

/**
 * Format date for database storage (YYYY-MM-DD)
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string for database
 */
export const formatDateForDatabase = (date) => {
  if (!date) return null;
  return formatInCDT(date, 'yyyy-MM-dd');
};

/**
 * Format datetime for database storage (YYYY-MM-DD HH:MM:SS)
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted datetime string for database
 */
export const formatDateTimeForDatabase = (date) => {
  if (!date) return null;
  return formatInCDT(date, 'yyyy-MM-dd HH:mm:ss');
};

/**
 * Get start of day in CDT
 * @param {Date|string} date - Date to get start of day for
 * @returns {Date} Start of day in CDT
 */
export const startOfDayCDT = (date) => {
  if (!date) return null;
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const cdtDate = toZonedTime(dateObj, CDT_TIMEZONE);
  const startOfDay = new Date(cdtDate);
  startOfDay.setHours(0, 0, 0, 0);
  return startOfDay;
};

/**
 * Get end of day in CDT
 * @param {Date|string} date - Date to get end of day for
 * @returns {Date} End of day in CDT
 */
export const endOfDayCDT = (date) => {
  if (!date) return null;
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const cdtDate = toZonedTime(dateObj, CDT_TIMEZONE);
  const endOfDay = new Date(cdtDate);
  endOfDay.setHours(23, 59, 59, 999);
  return endOfDay;
};

/**
 * Get current datetime string in CDT for form inputs (YYYY-MM-DDTHH:MM)
 * @returns {string} Current datetime string for form inputs
 */
export const getCurrentDateTimeForForm = () => {
  return formatInCDT(new Date(), 'yyyy-MM-dd\'T\'HH:mm');
};

/**
 * Default work date for new tickets: current calendar day in CDT.
 * @returns {string} Work date in YYYY-MM-DD format
 */
export const getWorkDate = () => {
  return formatInCDT(new Date(), 'yyyy-MM-dd');
}; 